import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiError, api, cents, points, rate, useCurrentCompany, useMe } from '../../lib'
import { ErrorBox, Loading } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import { useSide } from '../../app/side'
import { Logo, RateLine, StatusBadge } from './parts'
import { REASON_LABEL, type DirectoryEntry } from './types'

/** Directory page for one partner. `/directory/:slug` (public or signed in; the slug or the partner id). */
export function PartnerPage({ idOrSlug }: { idOrSlug?: string } = {}) {
  const { slug } = useParams()
  const key = idOrSlug ?? slug ?? ''
  const { me } = useMe()
  const company = useCurrentCompany()
  const { side } = useSide()
  const signedIn = !!me?.user && !!company && side === 'company'
  const qs = signedIn ? `?company_id=${encodeURIComponent(company!.id)}` : me?.user ? '?view=public' : ''
  const q = useQuery({
    queryKey: ['web', 'directory-entry', key, signedIn ? company!.id : null],
    queryFn: () => api<DirectoryEntry>('GET', `/api/directory/${encodeURIComponent(key)}${qs}`),
    enabled: me !== undefined && !!key,
  })

  if (q.isLoading || me === undefined) return <Loading />
  if (q.error instanceof ApiError && q.error.status === 404) {
    // a wrong or old link: say so and give the way back, not the server's message alone
    return (
      <div className="page narrow stack" data-testid="web-partner-missing">
        <h1>No such partner</h1>
        <p><Link to="/directory">See all partners</Link></p>
      </div>
    )
  }
  if (q.error) return <ErrorBox error={q.error} />
  const p = q.data!
  const rc = p.rate_card
  // "Counts what you pay X after you join" (key terms) and the would-have-earned line already say this.
  const reasons = (rc?.reasons ?? []).filter((r) => r !== 'before_platform_join')
  // A flat rate has one row: "months in a row" and a one-row table say nothing the rate doesn't.
  const steps = p.tiers.length > 1

  return (
    <div className="stack" data-testid="web-partner-page">
      <PageBand
        title={<span className="e-band-titled"><Logo name={p.name} url={p.logo_url} slug={p.slug} />{p.name}</span>}
        sub={p.description || p.status ? <>{p.description}{p.description && p.status ? ' ' : ''}<StatusBadge status={p.status} /></> : undefined}
        action={p.website && /^https?:\/\//i.test(p.website) ? <a className="e-btn" href={p.website} target="_blank" rel="noreferrer">Website</a> : undefined}
      />

      <section className="panel stack">
        <h2>Offer</h2>
        <dl className="facts">
          <dt>Rate</dt>
          <dd>
            <RateLine card={p} />
          </dd>
          {steps && p.tier_text && (
            <>
              <dt>So far</dt>
              <dd data-testid="web-tier">{p.tier_text}</dd>
            </>
          )}
          {p.next_step && (
            <>
              <dt>Next</dt>
              <dd data-testid="web-next-step">{p.next_step}</dd>
            </>
          )}
        </dl>
        {steps && (
          <table data-testid="web-tiers">
            <thead>
              <tr>
                <th>When</th>
                <th>Points</th>
              </tr>
            </thead>
            <tbody>
              {p.tiers.map((t) => (
                <tr key={t.condition}>
                  <td>{t.condition}</td>
                  <td className="num">{rate(t.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <ul className="muted" style={{ margin: 0, paddingLeft: 0 }} data-testid="web-key-terms">
          {p.key_terms.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </section>

      {rc && signedIn && ((rc.promo_rate && rc.promo_rate !== '0') || (rc.cap_remaining ?? null) !== null || reasons.length > 0) && (
        <section className="panel stack" data-testid="web-rate-card">
          {((rc.promo_rate && rc.promo_rate !== '0') || (rc.cap_remaining ?? null) !== null) && <h2>This month</h2>}
          <dl className="facts">
            {rc.promo_rate && rc.promo_rate !== '0' && (
              <>
                <dt>Includes extra</dt>
                <dd className="num">{rate(rc.promo_rate)}</dd>
              </>
            )}
            {rc.cap_remaining !== undefined && rc.cap_remaining !== null && (
              <>
                <dt>Points you can still earn</dt>
                <dd className="num">{points(rc.cap_remaining)}</dd>
              </>
            )}
          </dl>
          {reasons.length > 0 && (
            <ul className="muted" style={{ margin: 0, paddingLeft: 0 }}>
              {reasons.map((r) => (
                <li key={r}>{REASON_LABEL[r] ?? r}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {p.you && (
        <section className="panel stack" data-testid="web-you-here">
          <h2>Your spend and points</h2>
          <dl className="facts">
            <dt>Spend</dt>
            <dd className="num" data-testid="web-reported-spend">
              {cents(p.you.reported_spend_cents)}
            </dd>
            <dt>Cash back so far</dt>
            <dd className="num" data-testid="web-rebated">
              {cents(p.you.rebated_cents)}
            </dd>
            <dt>Spend left to pay back</dt>
            <dd className="num" data-testid="web-cash-available">
              {cents(p.you.cash_available_cents)}
            </dd>
            {p.you.points_earned !== null && (
              <>
                <dt>Points earned</dt>
                <dd className="num" data-testid="web-points-earned">
                  {points(p.you.points_earned)}
                </dd>
              </>
            )}
            <dt>Points on the way</dt>
            <dd className="num" data-testid="web-partner-awaiting">
              {points(p.you.points_awaiting_funding)}
            </dd>
          </dl>
          {(p.you.would_have_earned ?? 0) > 0 && (
            <p className="muted" style={{ margin: 0 }} data-testid="web-would-have-earned">
              Before you joined, your spend here would have earned <span className="num">{points(p.you.would_have_earned!)}</span> points.
            </p>
          )}
          {p.you.status === 'not_linked' && (p.limited ? (
            <p className="muted" style={{ margin: 0 }}>
              Not linked yet. It links when your first {p.name} receipt counts. <Link to="/invoices">Send receipts</Link>
            </p>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              Not linked yet. It links on its own when someone on your team signs in with the email {p.name} bills.{' '}
              <Link to="/team">Invite them</Link>
            </p>
          ))}
        </section>
      )}

      {!me?.user && (
        <p className="muted">
          <Link to="/login">Sign in</Link> to see your rate and the cash you can get back.
        </p>
      )}
    </div>
  )
}
